// @greenlight-spec: 15-database-schema.md
// @greenlight-spec: 28-it-dashboard-ui.md
import { z } from 'zod';
// Coding agents Agent setup / the install wizard can surface guidance for
// (docs/28 §Agent setup, docs/20 §agent-setup exposure). Presentation
// filtering only — see docs/decisions/0031, ADR-0040.
export const ALL_AGENT_KINDS = ['claude', 'codex', 'cursor', 'copilot', 'other'];
export const AgentKindSchema = z.enum(ALL_AGENT_KINDS);
// `contact` sits after `idp` so the initial administrator the IdP step recorded
// is available to prefill, and before `summary` because naming a reachable
// administrator is a precondition of finishing (docs/27 §API). `payment` is
// the last step before finishing and applies to direct installs only
// (ADR-0078).
export const INSTALL_STEPS = [
    'org_setup',
    'infrastructure',
    'dns',
    'control_plane_https',
    'verified_url_handoff',
    'idp',
    'github_app',
    'plugin_marketplace',
    'contact',
    'payment',
    'summary',
];
/**
 * Whether an install shows and needs a step. Only `payment` depends on
 * anything: a marketplace or internal install is billed elsewhere, so the
 * wizard never offers it a card step and finishing never waits on one.
 */
export function installStepApplies(step, billingProvider) {
    return step !== 'payment' || billingProvider === 'direct';
}
export const InstallStepStatusSchema = z.enum([
    'pending',
    'running',
    'blocked',
    'failed',
    'complete',
]);
export const InstallStepStateSchema = z.object({
    status: InstallStepStatusSchema,
    started_at: z.string().datetime().optional(),
    completed_at: z.string().datetime().optional(),
    last_error: z.object({ code: z.string(), message: z.string(), at: z.string() }).optional(),
    details: z.record(z.unknown()).optional(),
});
/**
 * The administrator Shift contacts about this install's subscription and billing.
 * Required to finish the wizard, forwarded to the fleet monitor over its own
 * authenticated call, and never carried in the health report (docs/27 §API).
 */
export const InstallContactSchema = z.object({
    contact_name: z.string().trim().min(1).max(200),
    contact_email: z.string().trim().min(3).max(320).email(),
});
export const InstallationStateSchema = z.object({
    status: z.enum(['not_started', 'in_progress', 'complete', 'failed']).optional(),
    steps: z
        .object({
        org_setup: InstallStepStateSchema.optional(),
        infrastructure: InstallStepStateSchema.optional(),
        dns: InstallStepStateSchema.optional(),
        control_plane_https: InstallStepStateSchema.optional(),
        verified_url_handoff: InstallStepStateSchema.optional(),
        idp: InstallStepStateSchema.optional(),
        github_app: InstallStepStateSchema.optional(),
        plugin_marketplace: InstallStepStateSchema.optional(),
        contact: InstallStepStateSchema.optional(),
        payment: InstallStepStateSchema.optional(),
        summary: InstallStepStateSchema.optional(),
    })
        .optional(),
});
/**
 * The install wizard's test sign-in (docs/12 §IdP OIDC App Registration): the
 * installer signs in through the saved provider and the callback evaluates the
 * ID token without writing a session or a user. `admin` must pass for the
 * step to complete; `user` is optional and never gates.
 */
export const IdpTestSignInPurposeSchema = z.enum(['admin', 'user']);
export const IdpTestSignInOutcomeSchema = z.object({
    status: z.enum(['started', 'passed', 'failed']),
    at: z.string().datetime(),
    /** The signed-in account; withheld from the public install status once installed. */
    email: z.string().optional(),
    /** The role the sign-in would carry, when it was not refused. */
    role: z.string().optional(),
    /** The refusal code, or the provider's own error code. */
    code: z.string().optional(),
    /** The values the token carried under the configured group claim, capped. */
    groups: z.array(z.string()).optional(),
    /** The account's `hd` domain on a Google sign-in. */
    hosted_domain: z.string().optional(),
    /** The saved values the sign-in was compared with, so a failure shows both sides. */
    expected: z
        .object({
        admin_email: z.string().optional(),
        org_admin_groups: z.array(z.string()).optional(),
        user_groups: z.array(z.string()).optional(),
        hosted_domains: z.array(z.string()).optional(),
    })
        .optional(),
    /** The provider's `error` and `error_description` when it redirected back with one. */
    provider_error: z.object({ error: z.string(), description: z.string().optional() }).optional(),
});
export const IdpTestSignInRecordSchema = z.object({
    admin: IdpTestSignInOutcomeSchema.optional(),
    user: IdpTestSignInOutcomeSchema.optional(),
});
/** Reads the step's recorded tests, tolerating a step written before they existed. */
export function parseIdpTestSignIn(details) {
    const parsed = IdpTestSignInRecordSchema.safeParse(details?.['test_sign_in'] ?? {});
    return parsed.success ? parsed.data : {};
}
//# sourceMappingURL=install.js.map